import React from 'react';

import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import {
    useGetChannelDetailsMutation,
    useGetChannelThresholdsQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import {
    setChartConfiguration,
    setGaugeWidgetConfiguration,
    setWidgetConfiguration,
    setWidgetLoader,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';

type ReturnProps = {
    isLoading: boolean;
};

export const useGetChannelValues = (widgetType: any): ReturnProps => {
    const dispatch = useAppDispatch();
    const channelPayload = useTypedSelector((state) => state[widgetType]);
    const multiDevice = useTypedSelector((state) => state['widgetConfiguration']['data']['multiDevice']);
    const selectedNode = useTypedSelector((state) => state['widgetConfiguration']['device']);

    const [getChannelValues, { isLoading, isSuccess, data, isError }] = useGetChannelDetailsMutation();

    const { data: thresholds }: any = useGetChannelThresholdsQuery(
        {
            channelId: channelPayload?.data?.channelId,
            deviceId: channelPayload?.data?.deviceId,
        },
        { skip: !channelPayload?.data?.deviceId || !channelPayload?.data?.channelId, refetchOnMountOrArgChange: true }
    );

    React.useEffect(() => {
        if (widgetType === 'charts' || widgetType === 'gauge') {
            widgetDispatcher();
        }

        if (
            (channelPayload?.primary || channelPayload?.secondary || channelPayload?.data) &&
            (selectedNode?.id || multiDevice)
        ) {
            void deviceDetailsHandler();
        }
    }, [channelPayload, selectedNode]);

    React.useEffect(() => {
        if (thresholds) {
            const customThreshold: any = {};
            thresholds?.data?.threshold?.forEach((item: any) => {
                if (item?.isEnabled) customThreshold[item.name] = item.value;
            });
            dispatch(
                setGaugeWidgetConfiguration({
                    threshold: customThreshold,
                    min: thresholds?.data?.min,
                    max: thresholds?.data?.max,
                })
            );
        }
    }, [thresholds]);

    React.useEffect(() => {
        if (isSuccess || isError) widgetDispatcher(data);
    }, [isSuccess, isError, data]);

    React.useEffect(() => {
        if (isError || (!selectedNode?.id && !multiDevice)) {
            dispatch(setWidgetLoader(false));
        }
    }, [isError, selectedNode?.id, multiDevice]);

    React.useEffect(() => {
        dispatch(setWidgetLoader(isLoading));
    }, [isLoading]);

    function getPayload(payload: any, key: any): any {
        channelPayload[key]?.forEach((item: any) => {
            const effectiveDeviceId = !multiDevice ? selectedNode?.id : item?.deviceId;
            if (!effectiveDeviceId || !item?.channelId) {
                return;
            }

            const selectedIndex = payload?.findIndex((subItem: any) => subItem.deviceId === effectiveDeviceId);
            const selectedItem = payload?.find((subItem: any) => subItem.deviceId === effectiveDeviceId);
            if (selectedItem) {
                payload?.[selectedIndex]?.channelId.push(item.channelId);
            } else {
                payload?.push({
                    deviceId: effectiveDeviceId,
                    channelId: [item.channelId],
                });
            }
        });
    }

    function setSingleDeviceChannelIds(payloadClone: any): void {
        if (multiDevice || !selectedNode?.id) return;

        ['primary', 'secondary'].forEach((key: string) => {
            payloadClone[key] = payloadClone[key]?.map((item: any) => ({
                ...item,
                // In single-device mode, always align channel ownership to the active preview device.
                deviceId: selectedNode.id,
            }));
        });
    }

    function widgetDispatcher(channelDetails?: any): boolean {
        const channelPayloadClone = JSON.parse(JSON.stringify(channelPayload));
        delete channelPayloadClone['multiDevice'];
        switch (widgetType) {
            case 'details':
            case 'properties':
            case 'settings':
            case 'command_bar':
                setSingleDeviceChannelIds(channelPayloadClone);
                dispatch(
                    setWidgetConfiguration({ channelPayload: channelPayloadClone, details: channelDetails, widgetType })
                );
                return true;
            case 'gauge': {
                if (!channelDetails) {
                    dispatch(
                        setGaugeWidgetConfiguration({
                            data: channelPayloadClone?.data,
                            type: channelPayloadClone?.type,
                            viewScale: channelPayloadClone?.viewScale,
                            viewThreshold: channelPayloadClone?.viewThreshold,
                        })
                    );
                    return true;
                }
                const gaugeChannel = channelDetails?.data?.[0]?.channels?.[0];
                dispatch(
                    setGaugeWidgetConfiguration({
                        data: {
                            ...channelPayloadClone?.data,
                            channelId: gaugeChannel?.channelId,
                            ...(gaugeChannel?.deviceId && {
                                deviceId: gaugeChannel.deviceId,
                            }),
                            ...(gaugeChannel?.value && {
                                value: parseFloat(gaugeChannel?.value ?? '0'),
                            }),
                            unit: gaugeChannel?.unit,
                        },
                        type: channelPayloadClone?.type,
                        viewScale: channelPayloadClone?.viewScale,
                        viewThreshold: channelPayloadClone?.viewThreshold,
                        ...(channelPayloadClone?.data?.multiDevice && {
                            multiDevice: channelPayloadClone?.data?.multiDevice,
                        }),
                    })
                );
                return true;
            }
            case 'charts':
                setSingleDeviceChannelIds(channelPayloadClone);
                dispatch(
                    setChartConfiguration({ channelPayload: channelPayloadClone, details: channelDetails, widgetType })
                );
                return true;
            default:
                return false;
        }
    }

    async function deviceDetailsHandler(): Promise<void> {
        let payload: any = [];
        Object.keys(channelPayload)?.forEach((key: any) => {
            if (key === 'primary' || key === 'secondary') {
                getPayload(payload, key);
            } else if (widgetType === 'gauge') {
                const channelId = channelPayload?.data?.channelId;
                if (channelId) {
                    payload = [
                        {
                            deviceId: channelPayload?.data?.deviceId,
                            channelId: [channelId],
                        },
                    ];
                }
            }
        });
        if (!payload.length) {
            widgetDispatcher();
            dispatch(setWidgetLoader(false));
            return;
        }
        widgetDispatcher();
        await getChannelValues(payload);
    }

    return {
        isLoading,
    };
};
